import type { ReactNode } from 'react';

/**
 * Field chrome shared by every control (build-plan §6.2 / §7.5): a real `<label htmlFor>` (fixes
 * Shopyan's missing label associations), optional helper text, an optional `n/max` counter, and an
 * inline `role="alert"` validation slot. The control itself is `children`.
 *
 * LAYOUT (Shopify-parity): `row` puts the label on the LEFT and the control on the RIGHT (the layout
 * Shopify uses for select / toggle / range / color / number); `stack` keeps the label above a full-width
 * control (for text / textarea / richtext / image). Helper + error always span the full width below.
 *
 * The counter sits BESIDE the `<label>`, never inside it, so the label's accessible name is exactly the
 * field label (and not "Heading 0/120").
 */
export function FieldRow({
  id,
  label,
  info,
  error,
  counter,
  children,
  labelAs = 'label',
  layout = 'stack',
  disabled = false,
}: {
  id: string;
  label: string;
  info?: string;
  error?: string;
  counter?: string;
  children: ReactNode;
  /** Use a plain <span> for controls that aren't a single labelable element (radiogroup, etc.). */
  labelAs?: 'label' | 'span';
  /** `row` = label left / control right (Shopify); `stack` = label above full-width control. */
  layout?: 'row' | 'stack';
  /**
   * The control is LOCKED by the store's configuration, not broken and not empty. Greys the whole row
   * (label included, so the row reads as one disabled thing) — the control itself still has to set its own
   * `disabled`, this is only the chrome. See `CheckboxControl` and `SectionForm`'s cart lock.
   */
  disabled?: boolean;
}) {
  const LabelTag = labelAs;
  // A LONG label crowds the inline `row` layout (e.g. "Background stars style"); auto-stack it above a
  // full-width control so the label is never squeezed beside the control. Short labels stay inline.
  const effLayout = layout === 'row' && label.length > 20 ? 'stack' : layout;
  return (
    // `data-field-key` is the inspector-side anchor for part-level selection: when the seller clicks a
    // field hotspot in the canvas, the Inspector scrolls to and flashes `[data-field-key="<field.id>"]`.
    // `id` is already the schema `field.id` every control passes here, so no control needs to change.
    <div
      className={`bld-field bld-field--${effLayout}${disabled ? ' bld-field--locked' : ''}`}
      data-field-key={id}
    >
      <div className="bld-field__label">
        <LabelTag htmlFor={labelAs === 'label' ? id : undefined}>{label}</LabelTag>
        {counter && <span className="bld-field__count">{counter}</span>}
      </div>
      <div className="bld-field__control">{children}</div>
      {info && <p className="bld-field__info">{info}</p>}
      {error && (
        <p className="bld-field__error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
